import {
  InitOverrideFunction,
  DefaultApi as JupiterApi,
  PriorityFeeWithMaxLamports,
  RequestOpts,
  createJupiterApiClient,
} from '@jup-ag/api';
import type { PlatformFeeAndAccounts } from '@jup-ag/common';
import {
  ExchangeParams,
  ExecuteParams,
  JUPITER_FEE_OWNER,
  JUPITER_V6_ERRORS,
  LAMPORTS_PER_SIGNATURE,
  MARKETS_URL,
  Owner,
  SwapMode,
  SwapResult,
  TOKEN_LIST_URL,
  calculateTransactionDepositAndFeeV2,
  executeTransaction,
  getReferralTokenAccount,
  getTransactionBlockhash,
} from '@jup-ag/common';
import { TransactionError, checkTxSize } from '@mercurial-finance/optimist';
import { TOKEN_PROGRAM_ID, NATIVE_MINT as WRAPPED_SOL_MINT } from '@solana/spl-token';
import { Blockhash, Connection, PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import JSBI from 'jsbi';
import promiseRetry from 'promise-retry';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { create } from 'superstruct';
import { getQuoteParamsOverrides } from './debug';
import { Errors } from './error';
import { QuoteResponse, QuoteResponseMeta, RoutePlanStep, RoutePlanWithMetadata, SwapInfo } from './utils/api';
import { getAssociatedTokenAddressSync } from './utils/ata';
import useDebounce from './utils/useDebounce';

export type JupiterError = (typeof Errors)[keyof typeof Errors];

interface UseJupiterResult {
  /** The quote response deserialized and the original */
  quoteResponseMeta?: QuoteResponseMeta;
  /** exchange function to submit transaction */
  exchange: (
    params: ExchangeParams<QuoteResponseMeta> &
      ExecuteParams & {
        prioritizationFeeLamports:
          | ExchangeParams<QuoteResponseMeta>['prioritizationFeeLamports']
          | {
              priorityLevelWithMaxLamports: PriorityFeeWithMaxLamports;
            }
          | undefined;
      },
  ) => Promise<SwapResult>;
  fetchQuote: () => Promise<{
    quoteResponse: QuoteResponse;
    original: any;
    timestamp: number;
  } | null>;
  /** fetchSwapTransaction function to fetch the transaction */
  fetchSwapTransaction: (
    params: Pick<
      ExchangeParams<QuoteResponseMeta>,
      'quoteResponseMeta' | 'userPublicKey' | 'wrapUnwrapSOL' | 'allowOptimizedWrappedSolTokenAccount'
    > & {
      enableSwapEndpointFee?: boolean;
      prioritizationFeeLamports:
        | ExchangeParams<QuoteResponseMeta>['prioritizationFeeLamports']
        | {
            priorityLevelWithMaxLamports: PriorityFeeWithMaxLamports;
          }
        | undefined;
      dynamicSlippage?: {
        minBps?: number;
        maxBps?: number;
      };
    },
  ) => Promise<
    | {
        dynamicSlippageReport?: {
          slippageBps?: number; // 5
          otherAmount?: number; // 999980000000000000
          simulatedIncurredSlippageBps?: number; // 12
          amplificationRatio?: string; // '1.999983256191180133562516026';
        };
        swapTransaction: VersionedTransaction | Transaction;
        blockhash: Blockhash;
        lastValidBlockHeight: number;
      }
    | { error: TransactionError }
  >;
  /** refresh function to refetch the prices */
  refresh: () => void;
  /** last refresh timestamp */
  lastRefreshTimestamp: number;
  /** loading state */
  loading: boolean;
  error: JupiterError | undefined;
  programIdToLabelMap: Map<string, string>;
  /* programId that is excluded */
  programIdsExcluded: Set<string>;
  setProgramIdsExcluded: React.Dispatch<React.SetStateAction<Set<string>>>;
}

interface JupiterProps {
  connection: Connection;
  platformFeeAndAccounts?: PlatformFeeAndAccounts;
  wrapUnwrapSOL?: boolean;
  onlyDirectRoutes?: boolean;
  asLegacyTransaction?: boolean;
  useSharedAccounts?: boolean;
  userPublicKey?: PublicKey;
  routeCacheDuration?: number;
  jupiterQuoteApiUrl?: string;
  children?: React.ReactNode;
  computeAutoSlippage?: boolean;
  whitelistedFeeMints?: string[];
}

const JupiterContext = createContext<
  | (Omit<JupiterProps, 'jupiterQuoteApiUrl'> & {
      connection: Connection;
      jupiterApiClient: JupiterApi;
      getPlatformFeeAccount: (platformFeeMint: string) => Promise<PublicKey | undefined>;
      programIdToLabelMap: Map<string, string>;
      error: JupiterError | undefined;
      programIdsExcluded: Set<string>;
      setError: (error?: JupiterError) => void;
      setProgramIdsExcluded: React.Dispatch<React.SetStateAction<Set<string>>>;
      whitelistedFeeMints?: string[];
    })
  | null
>(null);

export const JupiterProvider: React.FC<JupiterProps> = ({
  onlyDirectRoutes,
  userPublicKey,
  jupiterQuoteApiUrl,
  children,
  ...jupiterLoadProps
}) => {
  const [programIdToLabelMap, setProgramIdToLabelMap] = useState(new Map<string, string>());
  const [error, setError] = useState<JupiterError>();
  const [programIdsExcluded, setProgramIdsExcluded] = useState<Set<string>>(new Set());
  const platformFeeAndAccountsRef = useRef<PlatformFeeAndAccounts>();

  useEffect(() => {
    platformFeeAndAccountsRef.current = jupiterLoadProps.platformFeeAndAccounts;
  }, [jupiterLoadProps.platformFeeAndAccounts]);

  const setPlatformFeeAccount = useCallback((mint: string, platformFeeAccount: PublicKey | null) => {
    if (!platformFeeAndAccountsRef.current) return;
    if (!platformFeeAndAccountsRef.current.feeAccounts) platformFeeAndAccountsRef.current.feeAccounts = new Map();
    platformFeeAndAccountsRef.current.feeAccounts.set(mint, platformFeeAccount);
  }, []);

  const getPlatformFeeAccount = useCallback(async (platformFeeMint: string) => {
    const platformFeeAndAccounts = platformFeeAndAccountsRef.current;
    const platformFeeAccount = platformFeeAndAccounts?.feeAccounts?.get(platformFeeMint);
    const platformFeeBps = platformFeeAndAccounts?.feeBps;
    if (platformFeeAccount) return platformFeeAccount;
    if (platformFeeAccount === null || !platformFeeBps) return;

    if (platformFeeAndAccounts?.referralAccount) {
      const referralTokenAccount = await getReferralTokenAccount({
        connection: jupiterLoadProps.connection,
        referralAccount: platformFeeAndAccounts.referralAccount,
        mint: new PublicKey(platformFeeMint),
      });
      setPlatformFeeAccount(platformFeeMint, referralTokenAccount);
      if (referralTokenAccount) return referralTokenAccount;
    }
    return;
  }, []);

  const jupiterApiClient = useMemo(() => {
    if (jupiterQuoteApiUrl) {
      return createJupiterApiClient({
        basePath: jupiterQuoteApiUrl,
      });
    }
    return createJupiterApiClient();
  }, [jupiterQuoteApiUrl]);

  useEffect(() => {
    // reset when asLegacyTransaction toggled
    setProgramIdsExcluded((set) => {
      if (set.size) {
        return new Set();
      }
      return set;
    });
  }, [jupiterLoadProps.asLegacyTransaction]);

  useEffect(() => {
    async function fetchProgramIdToLabelMap() {
      const programIdToLabel = await jupiterApiClient.programIdToLabelGet();
      const programIdToLabelMap = new Map(Object.entries(programIdToLabel));
      setProgramIdToLabelMap(programIdToLabelMap);
    }
    fetchProgramIdToLabelMap();
  }, [jupiterApiClient]);

  return (
    <JupiterContext.Provider
      value={{
        ...jupiterLoadProps,
        getPlatformFeeAccount,
        userPublicKey,
        programIdToLabelMap,
        jupiterApiClient,
        error,
        setError,
        onlyDirectRoutes,
        programIdsExcluded,
        setProgramIdsExcluded,
        whitelistedFeeMints: jupiterLoadProps.whitelistedFeeMints,
      }}
    >
      {children}
    </JupiterContext.Provider>
  );
};

export interface UseJupiterProps {
  amount: JSBI;
  inputMint: PublicKey | undefined;
  outputMint: PublicKey | undefined;
  autoSlippage?: boolean;
  maxAutoSlippageBps?: number;
  autoSlippageCollisionUsdValue?: number;
  slippageBps?: number;
  /* inputAmount is being debounced, debounceTime 0 to disable debounce */
  debounceTime?: number;
  swapMode?: SwapMode;
  maxAccounts?: number;
  disabledProgramIds?: string[];
  computeAutoSlippage?: boolean;
  minimizeSlippage?: boolean; // default to false
  preferLiquidDexes?: boolean;
}

export const useJupiterClient = () => {
  const context = useContext(JupiterContext);
  if (!context) {
    throw new Error('JupiterProvider is required');
  }
  return context.jupiterApiClient;
};

export const useJupiter = ({
  amount,
  inputMint,
  outputMint,
  autoSlippage,
  maxAutoSlippageBps,
  autoSlippageCollisionUsdValue,
  slippageBps,
  swapMode,
  debounceTime = 250,
  maxAccounts,
  disabledProgramIds,
  computeAutoSlippage,
  minimizeSlippage,
  preferLiquidDexes,
}: UseJupiterProps): UseJupiterResult => {
  const context = useContext(JupiterContext);
  const [loading, setLoading] = useState(false);
  const [quoteResponseMeta, setQuoteResponseMeta] = useState<QuoteResponseMeta>();
  const [refreshCount, setRefreshCount] = useState<number>(0);
  // lastRefreshCount indicate when the last refresh was triggered on which refreshCount
  const lastRefreshCount = useRef<number>(refreshCount);

  const [debouncedAmount, debouncedInputMint, debouncedOutputMint, debouncedSwapMode] = useDebounce(
    React.useMemo(() => {
      // called immediaetly instead of waiting debounce because we want to show the loading state
      if (JSBI.greaterThan(amount, JSBI.BigInt(0))) {
        setLoading(true);
      }
      return [amount, inputMint, outputMint, swapMode];
    }, [amount.toString(), inputMint?.toBase58(), outputMint?.toBase58(), swapMode]),
    debounceTime,
  );

  const lastRefreshTimestamp = useRef<number>(0);
  const lastQueryTimestamp = useRef<number>(0);

  if (!context) {
    throw new Error('JupiterProvider is required');
  }

  const {
    error,
    setError,
    routeCacheDuration = 0,
    onlyDirectRoutes,
    jupiterApiClient,
    userPublicKey,
    wrapUnwrapSOL = true,
    asLegacyTransaction,
    platformFeeAndAccounts,
    programIdToLabelMap,
    setProgramIdsExcluded,
    programIdsExcluded,
    whitelistedFeeMints,
  } = context;

  // lastRefreshCount to determine when the last refresh was triggered, reset this to -1 to trigger a re-fetch
  useEffect(() => {
    lastRefreshCount.current = -1;
  }, [
    [debouncedInputMint?.toString(), debouncedOutputMint?.toString()].sort().join('-'),
    autoSlippage,
    maxAutoSlippageBps,
    autoSlippageCollisionUsdValue,
    slippageBps,
  ]);

  useEffect(() => {
    // if now - lastRefreshTimestamp > routeCacheDuration, then we need to refresh
    if (lastRefreshTimestamp.current && new Date().getTime() - lastRefreshTimestamp.current >= routeCacheDuration) {
      lastRefreshCount.current = -1;
    }

    // don't set loading if there is no input amount
    if (JSBI.greaterThan(debouncedAmount, JSBI.BigInt(0))) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    refreshCount,
    debouncedAmount,
    slippageBps,
    debouncedInputMint,
    debouncedOutputMint,
    onlyDirectRoutes,
    asLegacyTransaction,
  ]);

  const fetchQuote = useCallback(async () => {
    try {
      if (JSBI.equal(debouncedAmount, JSBI.BigInt(0)) || error === Errors.INITIALIZE_ERROR) {
        return null;
      } else if (debouncedAmount) {
        if (!debouncedInputMint || !debouncedOutputMint) return null;
        if (slippageBps && autoSlippage) throw new Error('Cannot set both slippageBps and autoSlippage');
        if (!autoSlippage && maxAutoSlippageBps) throw new Error('Cannot set maxAutoSlippageBps without autoSlippage');

        const inputMint = debouncedInputMint.toBase58();
        const outputMint = debouncedOutputMint.toBase58();

        if (swapMode === SwapMode.ExactOut && maxAccounts) {
          console.warn('maxAccounts is not supported for ExactOut swap mode');
        }

        let quoteResponse: QuoteResponse;
        let original: any;

        const excludeDexes = Array.from([...programIdsExcluded, ...(disabledProgramIds || [])])
          .map((id) => programIdToLabelMap.get(id))
          .filter((id): id is string => Boolean(id)) as string[];

        const quoteParamsOverrides = getQuoteParamsOverrides();
        const apiClient = new Proxy(jupiterApiClient, {
          get: function (target, prop: keyof JupiterApi & 'request') {
            if (prop === 'request') {
              return (context: RequestOpts, initOverrides?: RequestInit | InitOverrideFunction) => {
                if (Object.keys(quoteParamsOverrides).length) {
                  console.log('Quoting with overrides:', quoteParamsOverrides);
                  Object.assign(context.query!, quoteParamsOverrides);
                }

                // @ts-expect-error
                return target.request(context, initOverrides);
              };
            }
            return Reflect.get(target, prop);
          },
        });

        // For quoting, we just check if the inputMint/ouptutMint is whitelisted
        // so we dont have to call gPA (getPlatformFeeAccount) for every quote
        const supportedMint = (swapMode === SwapMode.ExactOut ? [inputMint] : [inputMint, outputMint]).filter(
          (mint) => {
            if (whitelistedFeeMints) {
              return whitelistedFeeMints?.includes(mint);
            }
            // If user do not define whitelistedFeeMints, allow all
            return true;
          },
        );
        const platformFeeBps = supportedMint.length > 0 ? platformFeeAndAccounts?.feeBps : undefined;

        const response = await promiseRetry(
          (retry) =>
            apiClient
              .quoteGet({
                inputMint,
                outputMint,
                amount: debouncedAmount.toString() as any, // making it number for u64.MAX would cause precision err
                autoSlippage,
                maxAutoSlippageBps,
                autoSlippageCollisionUsdValue,
                slippageBps,
                swapMode,
                excludeDexes: excludeDexes.length ? [excludeDexes.filter(Boolean).join(',')] : undefined,
                onlyDirectRoutes,
                asLegacyTransaction,
                platformFeeBps,
                maxAccounts,
                computeAutoSlippage,
                minimizeSlippage,
                preferLiquidDexes,
              })
              .catch((e) => {
                retry(e);
              }),
          {
            retries: 1,
            minTimeout: 100,
          },
        );

        // deep copy as create is mutating
        original = Object.assign({}, response);
        quoteResponse = create(response, QuoteResponse);

        return { quoteResponse, original, timestamp: new Date().getTime() };
      }

      return null;
    } catch (error) {
      throw error;
    }
  }, [
    debouncedAmount,
    debouncedInputMint,
    debouncedOutputMint,
    debouncedSwapMode,
    slippageBps,
    maxAutoSlippageBps,
    swapMode,
    maxAccounts,
    userPublicKey,
    wrapUnwrapSOL,
    refreshCount,
    onlyDirectRoutes,
    asLegacyTransaction,
    programIdsExcluded,
    preferLiquidDexes,
  ]);

  useEffect(() => {
    let requestAborted = false;

    (async () => {
      if (JSBI.equal(debouncedAmount, JSBI.BigInt(0)) || error === Errors.INITIALIZE_ERROR) {
        setLoading(false);
        setQuoteResponseMeta(undefined);
      } else if (debouncedAmount) {
        if (!debouncedInputMint || !debouncedOutputMint) return;
        const lastUpdatedTime = new Date().getTime();
        lastQueryTimestamp.current = lastUpdatedTime;

        lastRefreshTimestamp.current = lastUpdatedTime;

        try {
          const fetchedQuote = await fetchQuote();
          if (!fetchedQuote?.quoteResponse || !fetchedQuote?.original || !fetchedQuote?.timestamp || requestAborted)
            return;

          setQuoteResponseMeta({
            quoteResponse: fetchedQuote.quoteResponse,
            original: fetchedQuote.original,
          });

          setError(undefined);
        } catch (e: any) {
          console.error(e);
          if (lastQueryTimestamp.current !== lastUpdatedTime) {
            return;
          }

          const haveErrorCode = await (async () => {
            try {
              if ('response' in e && e.response instanceof Response) {
                const errorObj = await e.response.json();
                return errorObj.errorCode || null;
              }
            } catch (error) {
              console.log('Error parsing error response', error);
              return null;
            }
          })();

          if (haveErrorCode) {
            setError(haveErrorCode);
          } else {
            setError(Errors.FETCH_ROUTES_ERROR);
          }

          // Clear routes when erring to avoid bad pricing
          setQuoteResponseMeta(undefined);
        } finally {
          if (lastQueryTimestamp.current !== lastUpdatedTime) {
            return;
          }
          lastRefreshCount.current = refreshCount;
          setLoading(false);
        }
      }
    })();

    return () => {
      requestAborted = true;
    };
  }, [fetchQuote]);

  const { exchange, fetchSwapTransaction } = useJupiterExchange(swapMode);

  return {
    exchange,
    fetchQuote,
    fetchSwapTransaction,
    refresh: React.useCallback(() => {
      if (!loading && lastRefreshTimestamp.current) {
        setRefreshCount((refreshCount) => refreshCount + 1);
      }
    }, [loading]),
    lastRefreshTimestamp: lastRefreshTimestamp.current,
    loading,
    quoteResponseMeta,
    error,
    programIdToLabelMap,
    setProgramIdsExcluded,
    programIdsExcluded,
  };
};

export const useJupiterExchange = (swapMode: SwapMode | undefined) => {
  const context = useContext(JupiterContext);
  if (!context) {
    throw new Error('JupiterProvider is required');
  }

  const {
    jupiterApiClient,
    userPublicKey,
    connection,
    wrapUnwrapSOL = true,
    asLegacyTransaction,
    platformFeeAndAccounts,
    useSharedAccounts,
    getPlatformFeeAccount,
    whitelistedFeeMints,
  } = context;

  const fetchSwapTransaction = useCallback(
    async ({
      quoteResponseMeta,
      userPublicKey,
      wrapUnwrapSOL: propsWrapUnwrapSOL,
      prioritizationFeeLamports,
      allowOptimizedWrappedSolTokenAccount,
      dynamicSlippage,
    }: Parameters<UseJupiterResult['fetchSwapTransaction']>[0]) => {
      if (!userPublicKey) throw new Error('User public key is required');
      const wrapAndUnwrapSol = Boolean(propsWrapUnwrapSOL ?? wrapUnwrapSOL ?? true);
      const { inputMint, outputMint } = quoteResponseMeta.quoteResponse;

      const supportedMints = (swapMode === SwapMode.ExactOut ? [inputMint] : [inputMint, outputMint]).filter((mint) => {
        if (whitelistedFeeMints) {
          return whitelistedFeeMints?.includes(mint.toString());
        }
        // If user do not define whitelistedFeeMints, allow all
        return true;
      });

      const feeAccount =
        supportedMints.length !== 0
          ? await (async () => {
              for (const mint of supportedMints) {
                const account = await getPlatformFeeAccount(mint.toBase58());
                // First `mint` is always the `inputMint`, we always prioritise `inputMint` as fees
                if (account) {
                  return account;
                }
              }
              return undefined;
            })()
          : undefined;

      const result = await promiseRetry(
        (retry) =>
          jupiterApiClient
            .swapPost({
              swapRequest: {
                quoteResponse: quoteResponseMeta.original as any,
                userPublicKey: userPublicKey.toBase58(),
                wrapAndUnwrapSol,
                feeAccount: feeAccount?.toBase58(),
                asLegacyTransaction,
                useSharedAccounts,
                dynamicComputeUnitLimit: true,
                allowOptimizedWrappedSolTokenAccount,
                prioritizationFeeLamports,
                dynamicSlippage,
                correctLastValidBlockHeight: true,
              },
            })
            .catch(async (res) => {
              try {
                // probably an error with the quote so we dont retry
                const { error } = await res.json();
                return { error: new TransactionError(error) };
              } catch (e) {
                // probably json is not parsable or the res is not an response object.
                return retry({
                  error: new TransactionError('Unknown error'),
                });
              }
            }),
        {
          retries: 1,
          minTimeout: 100,
        },
      );

      if ('error' in result) {
        return result;
      }

      const { swapTransaction: swapTransactionSerialized, lastValidBlockHeight } = result;

      const swapTransactionBuffer = Buffer.from(swapTransactionSerialized, 'base64');

      const swapTransaction = asLegacyTransaction
        ? Transaction.from(swapTransactionBuffer)
        : VersionedTransaction.deserialize(swapTransactionBuffer);

      checkTxSize(swapTransaction);

      return {
        swapTransaction,
        blockhash: getTransactionBlockhash(swapTransaction),
        lastValidBlockHeight,
        dynamicSlippageReport: result.dynamicSlippageReport,
      };
    },
    [wrapUnwrapSOL, asLegacyTransaction, useSharedAccounts, getPlatformFeeAccount, jupiterApiClient],
  );

  const exchange: UseJupiterResult['exchange'] = useCallback(
    async ({
      wallet,
      quoteResponseMeta,
      onTransaction,
      prioritizationFeeLamports,
      ...restExchangeProps
    }): Promise<SwapResult> => {
      if (!userPublicKey) throw new Error('User public key is required');
      if (!wallet) throw new Error('Wallet is required');

      const { inputMint, outputMint } = quoteResponseMeta.quoteResponse;

      const wrapAndUnwrapSol = Boolean(restExchangeProps.wrapUnwrapSOL ?? wrapUnwrapSOL ?? true);

      const [result, accountInfos] = await Promise.all([
        fetchSwapTransaction({
          quoteResponseMeta,
          userPublicKey,
          wrapUnwrapSOL: wrapAndUnwrapSol,
          prioritizationFeeLamports,
          ...restExchangeProps,
        }),
        connection.getMultipleAccountsInfo([inputMint, outputMint]),
      ]);

      if ('error' in result) {
        return result;
      }

      const { blockhash, lastValidBlockHeight, swapTransaction } = result;
      const [sourceAddress, destinationAddress] = [inputMint, outputMint].map((mint, idx) =>
        getAssociatedTokenAddressSync(mint, userPublicKey, accountInfos[idx]?.owner || TOKEN_PROGRAM_ID),
      );

      return executeTransaction({
        connection,
        wallet,
        onTransaction,
        inputMint,
        outputMint,
        sourceAddress,
        destinationAddress,
        swapTransaction,
        blockhashWithBlockHeight: { lastValidBlockHeight, blockhash },
        owner: new Owner(userPublicKey),
        wrapUnwrapSOL: wrapAndUnwrapSol,
      });
    },
    [userPublicKey, wrapUnwrapSOL, asLegacyTransaction, connection, platformFeeAndAccounts, jupiterApiClient, swapMode],
  );

  return { exchange, fetchSwapTransaction };
};

export type { QuoteResponse, QuoteResponseMeta, RoutePlanStep, RoutePlanWithMetadata, SwapInfo };

export type {
  ErrorDetails,
  Fee,
  IndexedRouteMap,
  Instruction,
  OnTransaction,
  SwapResult,
  TransactionFeeInfo,
} from '@jup-ag/common';

export { JUPITER_PROGRAM_V4_ID, JUPITER_PROGRAM_V6_ID, OPENBOOK_PROGRAM_ID } from '@jup-ag/common';

export {
  Errors,
  JUPITER_FEE_OWNER,
  JUPITER_V6_ERRORS,
  LAMPORTS_PER_SIGNATURE,
  MARKETS_URL,
  Owner,
  SwapMode,
  TOKEN_LIST_URL,
};

export const calculateFeeForSwap = (
  quoteResponse: QuoteResponse | undefined,
  mintToAtaMap: Map<string, string>,
  openbookMarketToOpenOrderMap: Map<string, string>,
  wrapUnwrapSOL: boolean,
  useSharedAccounts: boolean,
) => {
  const atasToBeCreated = new Set<string>();
  const openOrdersToBeCreated = new Set<string>();
  const isInputMintSOL = Boolean(wrapUnwrapSOL && quoteResponse?.inputMint.equals(WRAPPED_SOL_MINT));

  const walkRoutePlan = (routePlan: RoutePlanWithMetadata) => {
    if (!useSharedAccounts) {
      routePlan.forEach((routeStep) => {
        if (!mintToAtaMap.has(routeStep.swapInfo.outputMint)) {
          atasToBeCreated.add(routeStep.swapInfo.outputMint);
        }

        if (routeStep.swapInfo.label === 'Openbook') {
          if (!openbookMarketToOpenOrderMap.has(routeStep.swapInfo.ammKey.toBase58())) {
            openOrdersToBeCreated.add(routeStep.swapInfo.outputMint);
          }
        }
      });
    }
  };

  if (quoteResponse) {
    if (useSharedAccounts) {
      let numOfAtasToBeCreated = 0;
      if (
        quoteResponse.inputMint.equals(WRAPPED_SOL_MINT) ||
        (quoteResponse.outputMint.equals(WRAPPED_SOL_MINT) &&
          wrapUnwrapSOL &&
          !mintToAtaMap.has(WRAPPED_SOL_MINT.toBase58()))
      ) {
        numOfAtasToBeCreated = 1;
      }

      return calculateTransactionDepositAndFeeV2({
        numOfAtasToBeCreated,
        numOfOpenOrdersToBeCreated: 0,
        isInputMintSOL,
      });
    }

    if (
      quoteResponse.inputMint.equals(WRAPPED_SOL_MINT) &&
      wrapUnwrapSOL &&
      !mintToAtaMap.has(WRAPPED_SOL_MINT.toBase58())
    ) {
      atasToBeCreated.add(WRAPPED_SOL_MINT.toBase58());
    }

    walkRoutePlan(quoteResponse.routePlan);
  }

  return calculateTransactionDepositAndFeeV2({
    numOfAtasToBeCreated: atasToBeCreated.size,
    numOfOpenOrdersToBeCreated: openOrdersToBeCreated.size,
    isInputMintSOL,
  });
};
